import React, { useEffect, useState } from 'react';
import { BASE } from './api';

// ─── User Office Log Modal (Admin) ──────────────────────────────────────────
// Click "N in office" → pick a user → see their login/logout history,
// filterable by day or month.
export default function UserOfficeLogModal({ darkMode, T, onClose }) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [period, setPeriod] = useState('day');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers }).then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedUser) return;
    setLoadingLogs(true);
    fetch(`${BASE}/api/dashboard/login-history?user_id=${selectedUser.id}&period=${period}&date=${date}`, { headers })
      .then(r => r.json())
      .then(d => setLogs(Array.isArray(d) ? d : []))
      .catch(() => setLogs([]))
      .finally(() => setLoadingLogs(false));
  }, [selectedUser, period, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDT = (v) => v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDur = (mins) => mins == null ? 'Active' : mins >= 60 ? `${Math.floor(mins/60)}h ${mins%60}m` : `${mins}m`;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', zIndex: 1000, transform: 'translate(-50%, -50%)',
        width: 720, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        background: darkMode ? T.bg2 : '#fff', borderRadius: 16, boxShadow: '0 32px 80px rgba(0,0,0,0.25)', overflow: 'hidden'
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>
              {selectedUser ? (
                <>
                  <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 13, fontWeight: 600, marginRight: 8 }}>← Back</button>
                  {selectedUser.full_name}'s Login/Logout Log
                </>
              ) : 'Staff Office Log'}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: T.text3, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {!selectedUser ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {users.map(u => (
                <button key={u.id} onClick={() => setSelectedUser(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 20, background: T.bg3, border: `1px solid ${T.border}`, cursor: 'pointer' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: u.avatar_url ? 'transparent' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, overflow: 'hidden' }}>
                    {u.avatar_url
                      ? <img src={`${BASE}${u.avatar_url}`} alt={u.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%' }} />
                      : (u.full_name?.charAt(0)?.toUpperCase() || 'U')}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{u.full_name}</span>
                  <span style={{ fontSize: 11, color: T.text3, textTransform: 'capitalize' }}>{u.role}</span>
                </button>
              ))}
              {users.length === 0 && <p style={{ fontSize: 13, color: T.text3 }}>No users found.</p>}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                  {['day', 'month'].map(p => (
                    <button key={p} onClick={() => setPeriod(p)}
                      style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textTransform: 'capitalize', background: period === p ? '#6366f1' : 'transparent', color: period === p ? '#fff' : T.text2 }}>
                      {p}
                    </button>
                  ))}
                </div>
                <input type={period === 'month' ? 'month' : 'date'}
                  value={period === 'month' ? date.slice(0, 7) : date}
                  onChange={(e) => setDate(period === 'month' ? `${e.target.value}-01` : e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 12, background: T.bg, color: T.text }} />
              </div>

              {loadingLogs ? (
                <p style={{ fontSize: 13, color: T.text3 }}>Loading…</p>
              ) : logs.length === 0 ? (
                <p style={{ fontSize: 13, color: T.text3 }}>No login activity for this {period}.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: T.text3, borderBottom: `1px solid ${T.border}` }}>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Login</th>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Logout</th>
                      <th style={{ padding: '6px 8px', fontWeight: 700 }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '8px', color: T.text }}>{fmtDT(l.login_time)}</td>
                        <td style={{ padding: '8px', color: T.text }}>{fmtDT(l.logout_time)}</td>
                        <td style={{ padding: '8px', color: l.duration_minutes == null ? '#10b981' : T.text2, fontWeight: l.duration_minutes == null ? 700 : 400 }}>{fmtDur(l.duration_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
